import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export const useDebounce = (callback, values, delay) => {
    const firstRun = useRef(true);
    const callbackTimeout = useRef();

    useEffect(() => {
        if (firstRun.current) {
            firstRun.current = false;
            return;
        }

        if (callback) {
            clearTimeout(callbackTimeout.current);
            callbackTimeout.current = setTimeout(() => {
                callback();
            }, delay || 500);
        }

        return () => {
            clearTimeout(callbackTimeout.current);
        };
    }, values);
};

export const useNavigateOptional = () => {
    try {
        const navigate = useNavigate();

        return (path) => {
            if (path) {
                navigate(path);
            }
        };
    } catch (e) {
        return () => {};
    }
};

export const useLocationOptional = () => {
    try {
        return useLocation();
    } catch (e) {
        return {};
    }
};

export const useEventListener = (eventName, handler, element = window) => {
    const savedHandler = useRef();

    useEffect(() => {
        savedHandler.current = handler;
    }, [handler]);

    useEffect(
        () => {
            // Make sure element supports addEventListener
            const isSupported = element && element.addEventListener;
            if (!isSupported) return;
            // Create event listener that calls handler function stored in ref
            const eventListener = (event) => savedHandler.current(event);
            // Add event listener
            element.addEventListener(eventName, eventListener);
            // Remove event listener on cleanup
            return () => {
                element.removeEventListener(eventName, eventListener);
            };
        },
        [eventName, element] // Re-run if eventName or element changes
    );
};
